// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { organizationUserSchema, personSchema } from '@/account/api/types';
import { sparseAssetSchema } from '@/asset/api/types';
import { sparseProjectSchema } from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Enum for ticket categories
 */
export const ticketCategorySchema = z.enum([
  'TODO',
  'CHECK',
  'DEFECT',
  'MISC',
  'REPAIR',
]);
export type TicketCategory = z.infer<typeof ticketCategorySchema>;

/**
 * Enum for ticket statuses
 */
export const ticketStatusSchema = z.enum(['OPEN', 'CLOSED', 'COMPLETED']);
export type TicketStatus = z.infer<typeof ticketStatusSchema>;

export const TICKET_DESCRIPTION_MAX_LENGTH = 65536;
export const TICKET_DESCRIPTION_MAX_LENGTH_MESSAGE =
  'Description cannot exceed 65,536 characters';
export const ticketDescriptionInputSchema = z
  .string()
  .max(TICKET_DESCRIPTION_MAX_LENGTH, TICKET_DESCRIPTION_MAX_LENGTH_MESSAGE);

/**
 * Schema for a ticket
 */
export const ticketSchema = z.object({
  id: z.string(),
  key: z.string(),
  title: z.string().max(160),
  description: z.string(),
  category: ticketCategorySchema,
  status: ticketStatusSchema,
  assignedTo: organizationUserSchema.nullable(),
  createdAt: dateTimeSchema,
  createdBy: organizationUserSchema,
  closedAt: dateTimeSchema.nullable(),
  completedOn: dateSchema.nullable(),
  dueOn: dateSchema.nullable(),
  completedBy: organizationUserSchema.nullable(),
  closedBy: organizationUserSchema.nullable(),
  lastUpdatedBy: organizationUserSchema,
  lastUpdatedAt: dateTimeSchema,
  assets: z.array(sparseAssetSchema),
  projects: z.array(sparseProjectSchema),
  fileCount: z.number(),
  noteCount: z.number(),
});
export type Ticket = z.infer<typeof ticketSchema>;

export const createTicketSchema = z.object({
  title: ticketSchema.shape.title,
  description: ticketDescriptionInputSchema,
  assignedTo: ticketSchema.shape.assignedTo,
  category: ticketSchema.shape.category,
  assets: ticketSchema.shape.assets,
  projects: ticketSchema.shape.projects,
});

export type CreateTicketData = z.infer<typeof createTicketSchema>;

/**
 * Schema for a note
 */
export const noteSchema = z.object({
  id: z.string(),
  key: z.string(),
  content: z.string(),
  createdAt: dateTimeSchema,
  createdBy: personSchema,
  lastUpdatedAt: dateTimeSchema,
  lastUpdatedBy: personSchema,
});
